import React, { lazy } from "react"
import { Roles } from "app/models/user"
import Translation from "components/Translation"
import { Modules } from "app/redux/moduleContext/moduleContext.reducer"
import { ILayoutProps, ILayoutRouteProps } from "app/components/Layout/types"
import { RouteComponentProps, RouteProps } from "react-router-dom"
import TableLayout from "app/components/Layout/TableLayout"
import PersonalPlanningNavigationBar from "app/components/PersonalPlanningView/PersonalPlanningNavigation/PersonalPlanningNavigationBar/PersonalPlanningNavigationBar"
import MobilePlanningNavigation from "app/components/PlanningView/MobilePlanningNavigation/MobilePlanningNavigation"
import EmployeeSettingsTabs from "app/components/EmployeeSettings"
import PersonalAbsencesTabs from "app/components/PersonalAbsences/PersonalAbsencesTabs"
import PracticeSettingsTabs from "app/components/PracticeSettings"
import { EmployeeSettingsRoutes, PracticeSettingsRoutes } from "./types"

export interface RouteObject extends RouteProps, ILayoutRouteProps {
  breadcrumb?: {
    allowedRoles: Roles[]
    component: JSX.Element
  }
  component: React.LazyExoticComponent<(props: any) => JSX.Element | null>
  appBarComponent?: React.FC
  path: string
  exact: boolean
  isPrivate: boolean
  appUtilityBar?: React.ElementType
  layout?: React.FC<ILayoutProps>
  hiddenOnMobile?: boolean
}

export type RouteMatchProps = RouteComponentProps<any>

const routes: RouteObject[] = [
  {
    isPrivate: true,
    allowedRoles: [Roles.Planner, Roles.HROfficer],
    component: lazy(() => import("./Root")),
    exact: true,
    module: Modules.System,
    path: "/",
  },
  {
    isPrivate: false,
    component: lazy(() => import("./Callback")),
    exact: true,
    module: Modules.System,
    path: "/callback",
  },
  {
    isPrivate: false,
    component: lazy(() => import("./Logout")),
    exact: true,
    module: Modules.System,
    path: "/logout",
  },
  {
    isPrivate: true,
    allowedRoles: [Roles.Planner, Roles.HROfficer, Roles.User],
    component: lazy(() => import("./PersonalPlanning")),
    appBarComponent: PersonalPlanningNavigationBar,
    exact: true,
    module: Modules.PersonalPlanning,
    layout: TableLayout,
    path: "/personal-planning/:year/:week/:weekday?",
  },
  {
    isPrivate: true,
    allowedRoles: [Roles.Planner, Roles.HROfficer, Roles.User],
    component: lazy(() => import("./PlanningRedirect")),
    exact: false,
    module: Modules.PersonalPlanning,
    path: "/personal-planning",
  },
  {
    isPrivate: true,
    allowedRoles: [Roles.Planner, Roles.HROfficer, Roles.User],
    appBarComponent: MobilePlanningNavigation,
    component: lazy(() => import("./Planning")),
    exact: false,
    module: Modules.Planning,
    path: "/planning/:group?/:year/:week",
  },
  {
    isPrivate: true,
    allowedRoles: [Roles.Planner, Roles.HROfficer, Roles.User],
    component: lazy(() => import("./PlanningRedirect")),
    exact: true,
    module: Modules.Planning,
    path: "/planning",
  },
  {
    isPrivate: true,
    allowedRoles: [Roles.Planner, Roles.HROfficer],
    component: lazy(() => import("./AbsencesRedirect")),
    exact: true,
    module: Modules.Absences,
    path: "/absences",
  },
  {
    isPrivate: true,
    allowedRoles: [Roles.Planner, Roles.HROfficer],
    component: lazy(() => import("./Absences")),
    exact: true,
    module: Modules.Absences,
    path: "/absences/:year?/:month?",
    hiddenOnMobile: true,
  },
  {
    isPrivate: true,
    allowedRoles: [Roles.Planner],
    component: lazy(() => import("./Templates")),
    exact: true,
    module: Modules.Templates,
    path: "/templates",
    hiddenOnMobile: true,
  },
  {
    isPrivate: true,
    allowedRoles: [Roles.Planner],
    breadcrumb: {
      allowedRoles: [Roles.Planner],
      component: <Translation label="breadcrumb.overview" />,
    },
    component: lazy(() => import("./TemplatesDetail")),
    exact: true,
    module: Modules.Templates,
    path: "/templates/:id/edit/:group?",
    hiddenOnMobile: true,
  },
  {
    isPrivate: true,
    allowedRoles: [Roles.Planner, Roles.HROfficer],
    component: lazy(() => import("./Employees")),
    exact: true,
    module: Modules.Team,
    path: "/employees",
  },
  {
    isPrivate: true,
    allowedRoles: [Roles.Planner, Roles.HROfficer],
    appUtilityBar: PracticeSettingsTabs,
    component: lazy(() => import("./PracticeSettings/WorkingHours")),
    exact: true,
    module: Modules.Practices,
    path: `/practice-settings/${PracticeSettingsRoutes.WorkingHours}`,
  },
  {
    isPrivate: true,
    allowedRoles: [Roles.Planner, Roles.HROfficer],
    appUtilityBar: PracticeSettingsTabs,
    component: lazy(() => import("./PracticeSettings/General")),
    exact: true,
    module: Modules.Practices,
    path: `/practice-settings/${PracticeSettingsRoutes.General}`,
  },
  {
    isPrivate: true,
    allowedRoles: [Roles.Planner, Roles.HROfficer],
    appUtilityBar: PracticeSettingsTabs,
    component: lazy(() => import("./PracticeSettings/ClosingDays")),
    exact: true,
    module: Modules.Practices,
    path: `/practice-settings/${PracticeSettingsRoutes.ClosingDays}`,
  },
  {
    isPrivate: true,
    allowedRoles: [Roles.Planner, Roles.HROfficer],
    appUtilityBar: PracticeSettingsTabs,
    component: lazy(() => import("./PracticeSettings/HrConfiguration")),
    exact: true,
    module: Modules.Practices,
    path: `/practice-settings/${PracticeSettingsRoutes.HrConfiguration}`,
  },
  {
    isPrivate: true,
    allowedRoles: [Roles.User, Roles.Planner, Roles.HROfficer],
    component: lazy(() => import("./PersonalAbsences")),
    exact: true,
    appUtilityBar: PersonalAbsencesTabs,
    module: Modules.PersonalAbsences,
    path: `/${EmployeeSettingsRoutes.PersonalAbsences}`,
  },
  {
    isPrivate: true,
    allowedRoles: [Roles.Planner, Roles.HROfficer, Roles.User],
    component: lazy(() => import("./EmployeeSettings/EmployeeDetails")),
    appUtilityBar: EmployeeSettingsTabs,
    exact: true,
    module: Modules.Team,
    path: `/employees/:id/${EmployeeSettingsRoutes.EmployeeDetail}`,
  },
  {
    isPrivate: true,
    allowedRoles: [Roles.Planner, Roles.HROfficer, Roles.User],
    component: lazy(() => import("./EmployeeSettings/ContractDetails")),
    appUtilityBar: EmployeeSettingsTabs,
    exact: true,
    module: Modules.Team,
    path: `/employees/:id/${EmployeeSettingsRoutes.ContractDetail}`,
  },
  {
    isPrivate: true,
    allowedRoles: [Roles.Planner, Roles.HROfficer, Roles.User],
    component: lazy(() => import("./EmployeeSettings/LeaveEntitlementsDetails")),
    appUtilityBar: EmployeeSettingsTabs,
    exact: true,
    module: Modules.Team,
    path: `/employees/:id/${EmployeeSettingsRoutes.LeaveEntitlementsDetail}`,
  },
  {
    isPrivate: true,
    allowedRoles: [Roles.Planner, Roles.HROfficer],
    component: lazy(() => import("./ValidationPoc")),
    exact: true,
    module: Modules.Poc,
    path: "/form-validation-poc",
  },
]

export default routes
